import React, { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCircleInfo,
  faBug,
  faCircleCheck,
  faCircleExclamation,
  faCode,
  faDownload,
  faGaugeHigh,
  faFileLines,
  faFolderTree,
  faMagnifyingGlass,
  faRotateRight,
  faSkullCrossbones,
  faTableList,
  faTrashCan,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import InputField from "../../../components/InputField/InputField"
import { useConfirmation } from "../../../context/useConfirmationContext"
import { AdminLogs } from "../../../Objects/AdminDashboard/AdminLogs"

interface ServerLogsSectionProps {
  logs: AdminLogs
  onRefresh: () => void
  onDelete: (relativePath: string) => void
  onDownload: (relativePath: string, fileName: string) => void
  isDeleting: boolean
  isDownloading: boolean
}

type EntryFilter = "all" | "normal" | "validation" | "warn"

export const ServerLogsSection: React.FC<ServerLogsSectionProps> = ({
  logs,
  onRefresh,
  onDelete,
  onDownload,
  isDeleting,
  isDownloading,
}) => {
  const { t } = useTranslation("admin")

  const { setNewConfirmation } = useConfirmation()

  const [selectedFile, setSelectedFile] = useState<string>("")

  const [searchTerm, setSearchTerm] = useState<string>("")

  const [entryFilter, setEntryFilter] = useState<EntryFilter>("all")

  const isValidationEntry = (level: string, message: string) => {
    if (level.toUpperCase() === "WARN" && message.toUpperCase().includes("[VALIDATION]")) {
      return true
    }

    return message.toUpperCase().includes("VALIDATIONEXCEPTION")
  }

  const currentFile = useMemo(() => {
    const fallback = logs.files[0]

    const found = logs.files.find((item) => item.relativePath === selectedFile)

    return found ?? fallback ?? null
  }, [logs.files, selectedFile])

  const stats = useMemo(() => {
    const allEntries = logs.files.flatMap((file) => file.entries)

    return {
      total: allEntries.length,
      validation: allEntries.filter((entry) => isValidationEntry(entry.level, entry.message)).length,
      normal: allEntries.filter((entry) => !isValidationEntry(entry.level, entry.message)).length,
      info: allEntries.filter((entry) => entry.level === "INFO").length,
      warn: allEntries.filter((entry) => entry.level === "WARN").length,
      error: allEntries.filter((entry) => entry.level === "ERROR").length,
      fatal: allEntries.filter((entry) => entry.level === "FATAL").length,
      debug: allEntries.filter((entry) => entry.level === "DEBUG").length,
      raw: allEntries.filter((entry) => entry.level === "RAW").length,
    }
  }, [logs.files])

  const filteredEntries = useMemo(() => {
    const entries = currentFile?.entries ?? []
    const normalizedTerm = searchTerm.trim().toLowerCase()

    const entriesByType = entries.filter((entry) => {
      const validation = isValidationEntry(entry.level, entry.message)

      if (entryFilter === "validation") {
        return validation
      }

      if (entryFilter === "warn") {
        return entry.level === "WARN"
      }

      if (entryFilter === "normal") {
        return !validation
      }

      return true
    })

    if (!normalizedTerm) {
      return entriesByType
    }

    return entriesByType.filter((entry) => {
      const searchable = `${entry.level} ${entry.timestamp ?? ""} ${entry.lineNumber} ${entry.message}`.toLowerCase()

      return searchable.includes(normalizedTerm)
    })
  }, [currentFile?.entries, entryFilter, searchTerm])

  const levelClass = (level: string) => {
    if (level === "VALIDATION") {
      return "border-cyan-300 bg-cyan-50 text-cyan-900 ring-1 ring-cyan-200"
    }

    if (level === "ERROR") {
      return "border-red-300 bg-red-50 text-red-800 ring-1 ring-red-200"
    }

    if (level === "FATAL") {
      return "border-rose-400 bg-rose-100 text-rose-900 ring-1 ring-rose-200"
    }

    if (level === "WARN") {
      return "border-amber-300 bg-amber-50 text-amber-900 ring-1 ring-amber-200"
    }

    if (level === "INFO") {
      return "border-emerald-300 bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200"
    }

    if (level === "DEBUG") {
      return "border-violet-300 bg-violet-50 text-violet-900 ring-1 ring-violet-200"
    }

    return "border-slate-300 bg-slate-100 text-slate-800 ring-1 ring-slate-200"
  }

  const levelIcon = (level: string) => {
    if (level === "VALIDATION") {
      return faCircleInfo
    }

    if (level === "ERROR") {
      return faCircleExclamation
    }

    if (level === "FATAL") {
      return faSkullCrossbones
    }

    if (level === "WARN") {
      return faTriangleExclamation
    }

    if (level === "INFO") {
      return faCircleCheck
    }

    if (level === "DEBUG") {
      return faBug
    }

    return faCode
  }

  const toMb = (bytes: number) => `${(bytes / 1024 / 1024).toFixed(2)} MB`

  const getDisplayLevel = (level: string, message: string) => (isValidationEntry(level, message) ? "VALIDATION" : level)

  const handleDeleteCurrentLog = () => {
    if (!currentFile) {
      return
    }

    setNewConfirmation(
      t("server_logs_delete_confirm_title"),
      t("server_logs_delete_confirm_message", { fileName: currentFile.fileName }),
      () => onDelete(currentFile.relativePath),
    )
  }

  return (
    <div className="w-full bg-slate-50/60 p-1 md:p-0 lg:flex lg:h-full lg:min-h-0 lg:flex-col lg:overflow-hidden">
      <Animate type={AnimationTypes.fadeInTraslate} duration="1s">
        <div className="mx-1 mt-2 rounded-lg border border-slate-200 bg-white p-4 shadow-sm lg:shrink-0">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-extrabold uppercase tracking-wide text-slate-800">
                <FontAwesomeIcon icon={faTableList} className="mr-2 text-orange-500" />
                Central de logs do servidor
              </h3>

              <p className="text-[12px] text-slate-500">Leitura rápida dos arquivos .log sem acessar o servidor.</p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button
                className="rounded-lg bg-orange-500 px-3 py-2 text-xs font-bold text-white transition hover:bg-orange-600"
                onClick={onRefresh}
                data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.serverLogsSection.button.onRefresh"
              >
                <FontAwesomeIcon icon={faRotateRight} className="mr-2" />
                Atualizar agora
              </button>

              <button
                className="rounded-lg bg-sky-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                onClick={() => currentFile && onDownload(currentFile.relativePath, currentFile.fileName)}
                disabled={!currentFile || isDownloading || isDeleting}
                data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.serverLogsSection.button.currentFileOnDownloadCurrentFileRelativePathCurrentFileFileN"
              >
                <FontAwesomeIcon icon={faDownload} className="mr-2" />
                {isDownloading ? t("server_logs_downloading") : t("server_logs_download")}
              </button>

              <button
                className="rounded-lg bg-red-600 px-3 py-2 text-xs font-bold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                onClick={handleDeleteCurrentLog}
                disabled={!currentFile || isDeleting || isDownloading}
                data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.serverLogsSection.button.handleDeleteCurrentLog"
              >
                <FontAwesomeIcon icon={faTrashCan} className="mr-2" />
                {isDeleting ? t("server_logs_deleting") : t("server_logs_delete")}
              </button>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-2 text-xs text-slate-600 md:grid-cols-3">
            <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <FontAwesomeIcon icon={faFolderTree} className="mr-2 text-sky-600" />
              Pasta:{" "}
              <span className="inline-block max-w-full truncate align-bottom font-semibold" title={logs.logsRoot}>
                {logs.logsRoot || "-"}
              </span>
            </p>

            <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <FontAwesomeIcon icon={faFileLines} className="mr-2 text-emerald-600" />
              Arquivos: <span className="font-semibold">{logs.files.length}</span>
            </p>

            <p className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              Última captura:{" "}
              <span className="font-semibold">
                {logs.capturedAtUtc ? new Date(logs.capturedAtUtc).toLocaleString() : "-"}
              </span>
            </p>
          </div>
        </div>
      </Animate>

      <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
        <div className="mx-1 mt-3 grid grid-cols-2 gap-2 rounded-lg border border-slate-200 bg-white p-3 shadow-sm md:grid-cols-8 lg:shrink-0">
          <div className="rounded-lg border border-cyan-200 bg-cyan-50 px-2 py-1 text-xs font-bold text-cyan-900">
            <FontAwesomeIcon icon={faCircleInfo} className="mr-1" />
            VALIDATION: {stats.validation}
          </div>

          <div className="rounded-lg border border-sky-200 bg-sky-50 px-2 py-1 text-xs font-bold text-sky-900">
            <FontAwesomeIcon icon={faTableList} className="mr-1" />
            NORMAL: {stats.normal}
          </div>

          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-800">
            <FontAwesomeIcon icon={faCircleInfo} className="mr-1" />
            INFO: {stats.info}
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 px-2 py-1 text-xs font-bold text-amber-900">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1" />
            WARN: {stats.warn}
          </div>

          <div className="rounded-lg border border-red-200 bg-red-50 px-2 py-1 text-xs font-bold text-red-800">
            <FontAwesomeIcon icon={faCircleExclamation} className="mr-1" />
            ERROR: {stats.error}
          </div>

          <div className="rounded-lg border border-rose-300 bg-rose-100 px-2 py-1 text-xs font-bold text-rose-900">
            <FontAwesomeIcon icon={faSkullCrossbones} className="mr-1" />
            FATAL: {stats.fatal}
          </div>

          <div className="rounded-lg border border-violet-200 bg-violet-50 px-2 py-1 text-xs font-bold text-violet-900">
            <FontAwesomeIcon icon={faBug} className="mr-1" />
            DEBUG: {stats.debug}
          </div>

          <div className="rounded-lg border border-slate-300 bg-slate-100 px-2 py-1 text-xs font-bold text-slate-800">
            <FontAwesomeIcon icon={faGaugeHigh} className="mr-1" />
            RUN: {stats.raw}
          </div>
        </div>
      </Animate>

      {logs.files.length === 0 ? (
        <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
          <div className="mx-1 mt-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 shadow-sm lg:shrink-0">
            <FontAwesomeIcon icon={faTriangleExclamation} className="mr-2" />
            Nenhum arquivo .log encontrado no diretório configurado.
          </div>
        </Animate>
      ) : (
        <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
          <div className="mt-3 grid grid-cols-1 gap-3 px-1 lg:min-h-0 lg:flex-1 lg:grid-cols-[280px,1fr] lg:overflow-hidden">
            <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm lg:flex lg:min-h-0 lg:flex-col lg:overflow-hidden">
              <p className="mb-2 shrink-0 px-2 text-xs font-extrabold uppercase tracking-wide text-slate-700">
                Arquivos de log
              </p>

              <div className="space-y-2 py-2 pr-2 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
                {logs.files.map((file, index) => (
                  <Animate
                    key={file.relativePath}
                    type={AnimationTypes.fadeInTraslate}
                    duration={`${1.5 + index * 0.2}s`}
                  >
                    <button
                      className={`mx-1 w-[calc(100%-0.5rem)] overflow-hidden rounded-lg border px-3 py-2 text-left shadow-sm transition-transform duration-200 ease-out hover:scale-[1.02] ${
                        currentFile?.relativePath === file.relativePath
                          ? "border-orange-300 bg-orange-50 ring-2 ring-orange-200"
                          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                      }`}
                      onClick={() => setSelectedFile(file.relativePath)}
                      data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.serverLogsSection.button.setSelectedFileFileRelativePath"
                    >
                      <p className="truncate text-xs font-extrabold text-slate-900" title={file.fileName}>
                        <FontAwesomeIcon icon={faFileLines} className="mr-2 text-orange-600" />
                        {file.fileName}
                      </p>

                      <p className="mt-1 truncate text-[11px] text-slate-600" title={file.relativePath}>
                        {file.relativePath}
                      </p>

                      <p className="mt-1 text-[11px] font-semibold text-slate-700">
                        {toMb(file.fileSizeBytes)} • {file.totalLines} linhas
                      </p>

                      <p className="mt-1 text-[11px] text-slate-600">
                        Atualizado em {file.lastWriteAtUtc ? new Date(file.lastWriteAtUtc).toLocaleString() : "-"}
                      </p>
                    </button>
                  </Animate>
                ))}
              </div>
            </div>

            <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm lg:flex lg:min-h-0 lg:flex-col lg:overflow-hidden">
              <div className="mb-3 shrink-0 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800">
                <span className="font-bold">{currentFile?.fileName}</span>
                <span className="ml-2 text-slate-600">({stats.total} entradas totais carregadas)</span>

                {currentFile?.isTruncated && (
                  <span className="ml-2 rounded-full bg-amber-100 px-2 py-1 text-[10px] font-bold text-amber-900 ring-1 ring-amber-300">
                    Exibindo últimas linhas
                  </span>
                )}
              </div>

              <div className="mb-3 shrink-0">
                <InputField
                  type="text"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Buscar por erro, warning, linha, data ou trecho da mensagem..."
                  icon={faMagnifyingGlass}
                  maxLength={140}
                  animationDuration="0.25s"
                  descriptionKey="interactionDescriptions:pages.adminDashboard.sections.serverLogsSection.input.buscarPorErroWarningLinhaDataOuTrechoDaMensagem"
                />
                <p className="mt-1 px-1 text-[11px] text-slate-500">
                  Exibindo {filteredEntries.length} de {currentFile?.entries.length ?? 0} entradas.
                </p>
              </div>

              <div className="mb-3 flex shrink-0 flex-wrap gap-2">
                {[
                  { value: "all", label: "Todos" },
                  { value: "normal", label: "Normal" },
                  { value: "validation", label: "Validação" },
                  { value: "warn", label: "Warning" },
                ].map((item) => (
                  <button
                    key={item.value}
                    onClick={() => setEntryFilter(item.value as EntryFilter)}
                    className={`rounded-full border px-3 py-1 text-[11px] font-bold transition ${
                      entryFilter === item.value
                        ? "border-orange-300 bg-orange-100 text-orange-900 ring-1 ring-orange-200"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50"
                    }`}
                    data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.serverLogsSection.button.setEntryFilterItemValueAsEntryFilter"
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-2 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
                {filteredEntries.length > 0 ? (
                  filteredEntries.map((entry, index) => (
                    <div
                      key={`${entry.lineNumber}-${entry.timestamp ?? "sem-data"}-${entry.level}-${index}`}
                      className={`rounded-lg border-l-4 p-2 ${levelClass(getDisplayLevel(entry.level, entry.message))}`}
                    >
                      <p className="text-[10px] font-extrabold uppercase tracking-wide">
                        <FontAwesomeIcon
                          icon={levelIcon(getDisplayLevel(entry.level, entry.message))}
                          className="mr-1"
                        />
                        {getDisplayLevel(entry.level, entry.message)} • linha {entry.lineNumber}
                        {entry.timestamp ? ` • ${entry.timestamp}` : ""}
                      </p>

                      <p className="mt-1 whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">
                        {entry.message}
                      </p>
                    </div>
                  ))
                ) : (
                  <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    Nenhuma entrada encontrada para o filtro informado.
                  </div>
                )}
              </div>
            </div>
          </div>
        </Animate>
      )}
    </div>
  )
}
